/**
 * api.ts — typed client เรียก Worker API (แนบ Bearer จาก session)
 */
import type { PnlLine, ExpenseCode, CustomExpenseCodeInput } from '@rrp/shared';
import { rowToExpenseCode } from '@rrp/shared';
import { config } from './config.js';
import { getSession, signOut } from './auth.js';
import { getTenant } from './state.js';

export async function apiGet<T>(path: string): Promise<T> {
  const session = getSession();
  if (!session) throw new Error('ยังไม่ได้เข้าสู่ระบบ');
  const res = await fetch(`${config.apiBase}${path}`, {
    headers: { authorization: `Bearer ${session.accessToken}` },
  });
  return handle<T>(res);
}

export async function apiPost<T>(path: string, body: unknown): Promise<T> {
  const session = getSession();
  if (!session) throw new Error('ยังไม่ได้เข้าสู่ระบบ');
  const res = await fetch(`${config.apiBase}${path}`, {
    method: 'POST',
    headers: {
      authorization: `Bearer ${session.accessToken}`,
      'content-type': 'application/json',
    },
    body: JSON.stringify(body),
  });
  return handle<T>(res);
}

async function handle<T>(res: Response): Promise<T> {
  const body = (await res.json().catch(() => ({}))) as {
    ok?: boolean;
    data?: T;
    requestId?: string;
    error?: { message?: string; code?: string; fields?: { path: string; message: string }[] };
  };
  if (res.status === 401) {
    signOut();
    throw new Error('เซสชันหมดอายุ กรุณาเข้าสู่ระบบใหม่');
  }
  if (!res.ok || body.ok === false) {
    // แสดงข้อความหลัก + รายละเอียดราย field + request id (ให้ผู้ใช้แจ้งปัญหาได้)
    const parts = [body.error?.message || `เกิดข้อผิดพลาด (${res.status})`];
    const fields = body.error?.fields ?? [];
    if (fields.length > 0) parts.push(fields.map((f) => `${f.path}: ${f.message}`).join(' · '));
    const requestId = body.requestId ?? res.headers.get('x-request-id');
    if (requestId) parts.push(`(request ${requestId})`);
    throw new Error(parts.join(' — '));
  }
  return body.data as T;
}

export interface AccountContext {
  accountId: string;
  accountName: string;
  businesses: { id: string; name: string }[];
}

export function getContext(): Promise<{ accounts: AccountContext[] }> {
  return apiGet('/api/context');
}

export interface PnlPayload {
  lines: PnlLine[];
  completeness: { status: string; missingCostCount: number; affectedRevenue: string };
  kpis: Record<string, string | number | null>;
}

export interface ComparisonPayload {
  comparisonType: string;
  current: PnlPayload;
  comparison: PnlPayload & { range: { from: string; to: string } };
  deltas: Record<string, string | null>;
}

export function getPnlCompare(
  accountId: string,
  businessId: string,
  from: string,
  to: string,
  compare: string,
): Promise<ComparisonPayload> {
  const q = new URLSearchParams({ accountId, businessId, from, to, compare });
  return apiGet(`/api/pnl/compare?${q.toString()}`);
}

export function getPnlDaily(
  accountId: string,
  businessId: string,
  from: string,
  to: string,
): Promise<{ points: { date: string; sales: string }[] }> {
  const q = new URLSearchParams({ accountId, businessId, from, to });
  return apiGet(`/api/pnl/daily?${q.toString()}`);
}

export function getPnlByChannel(
  accountId: string,
  businessId: string,
  from: string,
  to: string,
): Promise<{ items: { channelCode: string; sales: string }[] }> {
  const q = new URLSearchParams({ accountId, businessId, from, to });
  return apiGet(`/api/pnl/by-channel?${q.toString()}`);
}

export function getPnl(
  accountId: string,
  businessId: string,
  from: string,
  to: string,
): Promise<PnlPayload> {
  const q = new URLSearchParams({ accountId, businessId, from, to });
  return apiGet(`/api/pnl?${q.toString()}`);
}

export interface SkuProfitRow {
  skuId: string | null;
  skuName: string;
  unitsSold: string;
  revenue: string;
  cogs: string;
  grossProfit: string;
  platformFees: string;
  netContribution: string;
  marginPct: number | null;
  missingCost: boolean;
}
export interface SkuProfitReport {
  rows: SkuProfitRow[];
  totals: {
    revenue: string;
    cogs: string;
    grossProfit: string;
    platformFees: string;
    netContribution: string;
  };
  missingCostCount: number;
}
/** กำไรราย SKU (ตัวทำเงิน/ตัวขาดทุน) — ใช้ active tenant + ช่วงวันที่ */
export function getSkuProfit(from: string, to: string): Promise<SkuProfitReport> {
  return apiGet(`/api/pnl/sku-profit?${q({ from, to })}`);
}

export interface PlanSummary {
  plan: string;
  status: string | null;
  limits: {
    label: string;
    pricePerMonth: number;
    businesses: number | null;
    shops: number | null;
    users: number | null;
    modules: string[];
    historyMonths: number | null;
    storageBytes: number;
    autoChannels: string[];
  };
  usage: { shops?: number; storageBytes?: number };
}
/** แพ็กเกจปัจจุบัน + limit + usage ของบัญชี (paywall UI) */
export function getPlan(): Promise<PlanSummary> {
  return apiGet(`/api/plan?${new URLSearchParams({ accountId: scope().accountId }).toString()}`);
}

// ---- CRUD resources (Phase 7) — scope ด้วย active tenant ----
type Row = Record<string, unknown>;
interface ListResult {
  items: Row[];
  total?: number;
}

function scope(): { accountId: string; businessId: string } {
  const t = getTenant();
  if (!t) throw new Error('ยังไม่ได้เลือกกิจการ (ต้องอยู่ในโหมดข้อมูลจริง)');
  return { accountId: t.accountId, businessId: t.businessId };
}
function q(extra: Record<string, string> = {}): string {
  return new URLSearchParams({ ...scope(), ...extra }).toString();
}

// entries (T090) — filter/pagination/edit/history
export interface EntryFilter {
  from?: string;
  to?: string;
  classification?: string;
  reviewStatus?: string;
  search?: string;
  limit?: number;
  offset?: number;
}
export const listEntries = (f: EntryFilter = {}): Promise<ListResult> => {
  const extra: Record<string, string> = {};
  for (const [k, v] of Object.entries(f)) if (v !== undefined && v !== '') extra[k] = String(v);
  return apiGet(`/api/entries?${q(extra)}`);
};
/** แจ้งว่าข้อมูลเปลี่ยน → หน้าแรก/รายงานรีเฟรชอัตโนมัติ (main.ts รับฟัง) */
export function notifyDataChanged(): void {
  try {
    window.dispatchEvent(new CustomEvent('rrp:data-changed'));
  } catch {
    /* ignore (non-browser env) */
  }
}
export const getEntry = (id: string): Promise<Row> => apiGet(`/api/entries/detail?${q({ id })}`);
export const createEntry = (body: Row): Promise<{ id: string }> =>
  apiPost<{ id: string }>('/api/entries', { ...scope(), ...body }).then((r) => {
    notifyDataChanged();
    return r;
  });
export const updateEntry = (id: string, patch: Row): Promise<Row> =>
  apiPost<Row>('/api/entries/update', { ...scope(), id, ...patch }).then((r) => {
    notifyDataChanged();
    return r;
  });
export const reverseEntry = (originalId: string, reason: string): Promise<{ id: string }> =>
  apiPost<{ id: string }>('/api/entries/reverse', { ...scope(), originalId, reason }).then((r) => {
    notifyDataChanged();
    return r;
  });

// attachments + export job (T104/T094) — ใช้ได้เมื่อเปิด R2 (ไม่งั้น endpoint คืน 404)
export interface AttachmentRow {
  id: string;
  objectKey: string;
  filename: string | null;
  contentType: string | null;
  sizeBytes: number | null;
  checksum: string | null;
  entryId?: string | null;
  docType?: string | null;
}
export async function uploadAttachment(params: {
  target: 'entry' | 'document';
  file: File;
  entryId?: string;
  docType?: string;
  refTable?: string;
  refId?: string;
}): Promise<AttachmentRow> {
  const session = getSession();
  if (!session) throw new Error('ยังไม่ได้เข้าสู่ระบบ');
  const s = scope();
  const fd = new FormData();
  fd.set('accountId', s.accountId);
  fd.set('businessId', s.businessId);
  fd.set('target', params.target);
  fd.set('file', params.file);
  if (params.entryId) fd.set('entryId', params.entryId);
  if (params.docType) fd.set('docType', params.docType);
  if (params.refTable) fd.set('refTable', params.refTable);
  if (params.refId) fd.set('refId', params.refId);
  const res = await fetch(`${config.apiBase}/api/attachments`, {
    method: 'POST',
    headers: { authorization: `Bearer ${session.accessToken}` },
    body: fd,
  });
  return handle<AttachmentRow>(res);
}
export const listEntryAttachments = (entryId: string): Promise<{ items: AttachmentRow[] }> =>
  apiGet(`/api/attachments?${q({ target: 'entry', entryId })}`);
export const listDocuments = (docType?: string): Promise<{ items: AttachmentRow[] }> =>
  apiGet(`/api/attachments?${q(docType ? { target: 'document', docType } : { target: 'document' })}`);
// หลักฐานของทรัพย์สิน (documents ผูก ref_table=fixed_assets) — T094
export const uploadAssetEvidence = (assetId: string, file: File): Promise<AttachmentRow> =>
  uploadAttachment({ target: 'document', file, refTable: 'fixed_assets', refId: assetId, docType: 'asset_evidence' });
export const listAssetEvidence = (assetId: string): Promise<{ items: AttachmentRow[] }> =>
  apiGet(`/api/attachments?${q({ target: 'document', refTable: 'fixed_assets', refId: assetId })}`);
export interface SignedUrl {
  url: string;
  expiresAt: string;
  filename: string | null;
  contentType: string | null;
}
export const getAttachmentUrl = (
  id: string,
  target: 'entry' | 'document' | 'export',
): Promise<SignedUrl> => apiGet(`/api/attachments/signed-url?${q({ id, target })}`);
export const createExportJob = (
  from: string,
  to: string,
  format: 'xlsx' | 'svg',
): Promise<SignedUrl> => apiPost('/api/export/job', { ...scope(), from, to, format });
/** signed URL ที่ API คืนเป็น path (/api/files?token=..) → ประกอบเป็น URL เต็มสำหรับดาวน์โหลด */
export const downloadHref = (signedPath: string): string => `${config.apiBase}${signedPath}`;

// TikTok live sync (T054/T056) — ใช้ได้เมื่อตั้ง credential แล้ว (ไม่งั้น endpoint คืน 404)
export interface TikTokShop {
  id: string;
  shop_id: string;
  shop_name: string | null;
  region: string | null;
  token_expires_at: string | null;
  is_active: boolean;
}
export const tiktokConnect = (): Promise<{ authorizeUrl: string }> =>
  apiPost('/api/tiktok/connect', { ...scope() });
/** เรียกหลัง TikTok redirect กลับเว็บ (authed) — ผูกร้านเข้าบัญชีที่เริ่ม flow */
export const tiktokComplete = (
  code: string,
  state: string,
): Promise<{ ok: boolean; shopsLinked?: number; reason?: string }> =>
  apiPost('/api/tiktok/callback', { code, state });
export const tiktokListShops = (): Promise<{ items: TikTokShop[] }> =>
  apiGet(`/api/tiktok/shops?${q()}`);
export const tiktokSync = (
  shopId: string,
): Promise<{
  skipped: boolean;
  itemCount: number;
  status?: string;
  products?: { skipped: boolean; itemCount: number; status?: string };
  settlements?: { skipped: boolean; itemCount: number; status?: string };
  refunds?: { skipped: boolean; itemCount: number; status?: string };
}> => apiPost('/api/tiktok/sync', { ...scope(), shopId });

// audit history (T090/T099)
export interface AuditRow {
  id: string;
  action: string;
  entityTable: string;
  entityId: string | null;
  actorId: string | null;
  reason: string | null;
  requestId: string | null;
  createdAt: string;
  oldData: unknown;
  newData: unknown;
}
export const listAudit = (
  f: { entityTable?: string; entityId?: string; action?: string; limit?: number; offset?: number } = {},
): Promise<{ items: AuditRow[]; total: number }> => {
  const extra: Record<string, string> = {};
  for (const [k, v] of Object.entries(f)) if (v !== undefined && v !== '') extra[k] = String(v);
  return apiGet(`/api/audit?${q(extra)}`);
};

// payroll (T091)
export const listPayroll = (): Promise<ListResult> => apiGet(`/api/payroll?${q()}`);
export const createPayroll = (body: Row): Promise<{ id: string; totals: Row }> =>
  apiPost('/api/payroll', { ...scope(), ...body });
export const updatePayroll = (id: string, patch: Row): Promise<Row> =>
  apiPost('/api/payroll/update', { ...scope(), id, ...patch });

// catalog: SKU master / alias / shops (T092)
export interface SkuStatusRow {
  id: string;
  skuCode: string;
  name: string | null;
  unit: string;
  isActive: boolean;
  unitCost: string | null;
  hasCost: boolean;
  historyCount: number;
}
export const listSkus = (
  f: { search?: string; missingCostOnly?: boolean; asOf?: string; limit?: number; offset?: number } = {},
): Promise<{ items: SkuStatusRow[]; total: number; missingCostCount: number; asOf: string }> => {
  const extra: Record<string, string> = {};
  for (const [k, v] of Object.entries(f)) if (v !== undefined && v !== '' && v !== false) extra[k] = String(v);
  return apiGet(`/api/skus?${q(extra)}`);
};
export const createSku = (body: Row): Promise<{ id: string }> =>
  apiPost('/api/skus', { ...scope(), ...body });
export const listAliases = (skuId?: string): Promise<{ items: Row[] }> =>
  apiGet(`/api/skus/aliases?${q(skuId ? { skuId } : {})}`);
export const createAlias = (body: Row): Promise<{ id: string }> =>
  apiPost('/api/skus/aliases', { ...scope(), ...body });
export const listShops = (): Promise<{ items: Row[] }> => apiGet(`/api/shops?${q()}`);

// SKU cost (T092)
export const listCost = (skuId: string): Promise<{ items: Row[] }> =>
  apiGet(`/api/skus/cost?${q({ skuId })}`);
export const addCost = (body: Row): Promise<{ id: string }> =>
  apiPost('/api/skus/cost', { ...scope(), ...body });
export const resolveCost = (skuId: string, date: string): Promise<{ unitCost: string | null }> =>
  apiGet(`/api/skus/cost/resolve?${q({ skuId, date })}`);

// purchases & inventory (T093)
export const listPurchases = (): Promise<ListResult> => apiGet(`/api/purchases?${q()}`);
export const createPurchase = (body: Row): Promise<{ id: string; totals: Row }> =>
  apiPost('/api/purchases', { ...scope(), ...body });
export const getPurchase = (id: string): Promise<Row> => apiGet(`/api/purchases/detail?${q({ id })}`);
export const listMovements = (skuId?: string): Promise<ListResult> =>
  apiGet(`/api/inventory/movements?${q(skuId ? { skuId } : {})}`);
export const createMovement = (body: Row): Promise<{ id: string }> =>
  apiPost('/api/inventory/movements', { ...scope(), ...body });
export const stockBalance = (skuId: string): Promise<{ balance: string }> =>
  apiGet(`/api/inventory/balance?${q({ skuId })}`);
export const listCounts = (skuId?: string): Promise<ListResult> =>
  apiGet(`/api/inventory/counts?${q(skuId ? { skuId } : {})}`);
export const createCount = (body: Row): Promise<{ id: string }> =>
  apiPost('/api/inventory/counts', { ...scope(), ...body });
export const listCogsRecon = (): Promise<ListResult> => apiGet(`/api/cogs-recon?${q()}`);
export const createCogsRecon = (body: Row): Promise<Row> =>
  apiPost('/api/cogs-recon', { ...scope(), ...body });

// assets (T094)
export const listAssets = (reviewStatus?: string): Promise<ListResult> =>
  apiGet(`/api/assets?${q(reviewStatus ? { reviewStatus } : {})}`);
export const createAsset = (body: Row): Promise<{ id: string; reviewStatus: string }> =>
  apiPost('/api/assets', { ...scope(), ...body });
export const approveAsset = (id: string, reason: string): Promise<Row> =>
  apiPost('/api/assets/update', { ...scope(), id, approved: true, reason });
export const genSchedule = (assetId: string): Promise<Row> =>
  apiPost('/api/assets/schedule', { ...scope(), assetId });
export const listSchedule = (assetId: string): Promise<ListResult> =>
  apiGet(`/api/assets/schedule?${q({ assetId })}`);
export const disposeAsset = (body: Row): Promise<Row> =>
  apiPost('/api/assets/dispose', { ...scope(), ...body });
export const listDisposals = (): Promise<ListResult> => apiGet(`/api/assets/disposals?${q()}`);

// tax (T095)
export const listTaxLedger = (): Promise<ListResult> => apiGet(`/api/tax/ledger?${q()}`);
export const createTaxLedger = (body: Row): Promise<{ id: string; pnlImpact: boolean }> =>
  apiPost('/api/tax/ledger', { ...scope(), ...body });
export const confirmTaxLedger = (id: string, reason: string): Promise<Row> =>
  apiPost('/api/tax/ledger/confirm', { ...scope(), id, reason });
export const listTaxFilings = (): Promise<ListResult> => apiGet(`/api/tax/filings?${q()}`);
export const createTaxFiling = (body: Row): Promise<{ id: string }> =>
  apiPost('/api/tax/filings', { ...scope(), ...body });
export const listWhtCerts = (): Promise<ListResult> => apiGet(`/api/tax/wht-certs?${q()}`);
export const createWhtCert = (body: Row): Promise<{ id: string }> =>
  apiPost('/api/tax/wht-certs', { ...scope(), ...body });

// allocation (T098)
export const listRules = (): Promise<ListResult> => apiGet(`/api/allocation/rules?${q()}`);
export const createRule = (body: Row): Promise<{ id: string; version: number }> =>
  apiPost('/api/allocation/rules', { ...scope(), ...body });
export const runAllocation = (body: Row): Promise<Row> =>
  apiPost('/api/allocation/run', { ...scope(), ...body });
export const listAllocationResults = (ruleId: string): Promise<{ items: Row[] }> =>
  apiGet(`/api/allocation/results?${q({ ruleId })}`);

// mapping (T098)
export const listPnlMappings = (): Promise<{ items: Row[] }> => apiGet(`/api/mappings/pnl?${q()}`);
export const savePnlMapping = (body: Row): Promise<{ id: string; created: boolean }> =>
  apiPost('/api/mappings/pnl', { ...scope(), ...body });
export const listColumnTemplates = (): Promise<{ items: Row[] }> =>
  apiGet(`/api/mappings/columns?${q()}`);
export const saveColumnTemplate = (body: Row): Promise<{ id: string; created: boolean }> =>
  apiPost('/api/mappings/columns', { ...scope(), ...body });

// orders & payouts (T096)
export interface OrderFilter {
  from?: string;
  to?: string;
  channelCode?: string;
  search?: string;
  unpaidOnly?: boolean;
  limit?: number;
  offset?: number;
}
export const listOrders = (f: OrderFilter = {}): Promise<ListResult> => {
  const extra: Record<string, string> = {};
  for (const [k, v] of Object.entries(f)) {
    if (v !== undefined && v !== '' && v !== false) extra[k] = String(v);
  }
  return apiGet(`/api/orders?${q(extra)}`);
};
export const getOrder = (id: string): Promise<Row> => apiGet(`/api/orders/detail?${q({ id })}`);
export const listSettlements = (): Promise<{ items: Row[] }> => apiGet(`/api/settlements?${q()}`);
export const listRefunds = (f: { from?: string; to?: string } = {}): Promise<{ items: Row[] }> => {
  const extra: Record<string, string> = {};
  for (const [k, v] of Object.entries(f)) if (v) extra[k] = String(v);
  return apiGet(`/api/refunds?${q(extra)}`);
};

// import & sync (T097)
export interface ImportPlanCounts {
  new: number;
  updated: number;
  unchanged: number;
  error: number;
  review: number;
  duplicateInFile: number;
}
export interface ImportRecord {
  externalId: string;
  rowIndex: number;
  issues: { level: string; field?: string; message: string }[];
  data: Row;
}
export interface ImportPreviewResult {
  batchId: string;
  plan: {
    create: ImportRecord[];
    update: ImportRecord[];
    unchanged: ImportRecord[];
    errors: ImportRecord[];
    review: ImportRecord[];
    duplicatesInFile: ImportRecord[];
    counts: ImportPlanCounts;
    aggregateHash: string;
  };
  coverage: {
    rowCount: number;
    from?: string;
    to?: string;
    errorCount: number;
    reviewCount: number;
    completeness: 'complete' | 'incomplete' | 'needs_review';
  };
  duplicatePeriod: boolean;
  appliedCount?: number;
}
export const importPreview = (body: Row): Promise<ImportPreviewResult> =>
  apiPost('/api/import/preview', { ...scope(), ...body });
export const importConfirm = (body: Row): Promise<ImportPreviewResult> =>
  apiPost('/api/import/confirm', { ...scope(), ...body });
export const importRollback = (batchId: string, reason: string): Promise<Row> =>
  apiPost('/api/import/rollback', { ...scope(), batchId, reason });
export const listBatches = (): Promise<{ items: Row[] }> => apiGet(`/api/import/batches?${q()}`);
export const syncStatus = (): Promise<{ jobs: Row[]; coverage: Row[] }> =>
  apiGet(`/api/sync/status?${q()}`);

// periods (T076/T099)
export const listPeriods = (): Promise<{ items: Row[] }> => apiGet(`/api/periods?${q()}`);
export const closePeriod = (year: number, month: number): Promise<Row> =>
  apiPost('/api/periods/close', { ...scope(), year, month });

// drill-down (T075) — เจาะ P&L line ลงรายการ source
export interface DrilldownItem {
  label: string;
  date: string | null;
  amount: string;
}
export const drilldown = (
  line: string,
  from: string,
  to: string,
): Promise<{ lineKey: string; total: string; count: number; items: DrilldownItem[] }> =>
  apiGet(`/api/pnl/drilldown?${q({ line, from, to })}`);

// export SVG ของรายงาน (T102) — web แปลงเป็น PNG ต่อ
export async function exportPnlSvg(from: string, to: string): Promise<string> {
  const session = getSession();
  if (!session) throw new Error('ยังไม่ได้เข้าสู่ระบบ');
  const res = await fetch(`${config.apiBase}/api/export/pnl.svg?${q({ from, to })}`, {
    headers: { authorization: `Bearer ${session.accessToken}` },
  });
  if (!res.ok) throw new Error('สร้างภาพรายงานไม่สำเร็จ');
  return res.text();
}

// export xlsx (T101) — คืน Blob ให้ browser ดาวน์โหลด
export async function exportPnlXlsx(from: string, to: string): Promise<Blob> {
  const session = getSession();
  if (!session) throw new Error('ยังไม่ได้เข้าสู่ระบบ');
  const res = await fetch(`${config.apiBase}/api/export/pnl.xlsx?${q({ from, to })}`, {
    headers: { authorization: `Bearer ${session.accessToken}` },
  });
  if (!res.ok) throw new Error('ส่งออกไม่สำเร็จ');
  return res.blob();
}

// โค้ดค่าใช้จ่ายที่ผู้ใช้เพิ่มเอง (Simple Mode) — backend ต่อกิจการ
export async function listCustomCodesApi(): Promise<ExpenseCode[]> {
  const r = await apiGet<{ items: Row[] }>(`/api/custom-codes?${q()}`);
  return r.items.map((row) => rowToExpenseCode(row as Parameters<typeof rowToExpenseCode>[0]));
}
export function saveCustomCodeApi(
  input: CustomExpenseCodeInput,
): Promise<{ id: string; created: boolean }> {
  return apiPost('/api/custom-codes', { ...scope(), ...input });
}

// สมัครใช้งานเอง: สร้างกิจการ + ร้านแรก (ไม่มี tenant scope — เรียกก่อนมีกิจการ)
export function onboardingApi(
  accountName: string,
  businessName: string,
): Promise<{ accountId: string; businessId: string; created: boolean }> {
  return apiPost('/api/onboarding', { accountName, businessName });
}
